import React, { useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import {
  Lock, CreditCard, Plus, FileText, Settings, BarChart2,
  Layers, Shield, Key, Receipt, ShieldAlert, Store,
  ChevronLeft, ChevronRight, LogOut, Bell, Menu, X, ShoppingCart, Wallet, ArrowLeft
} from "lucide-react";
import { base44 } from "@voult/api/base44Client";
import { useAuth } from "@voult/lib/AuthContext";

const NAV = [
  { label: "My Vault", path: "/voult/vault-list", icon: CreditCard },
  { label: "Checkout", path: "/voult/checkout", icon: ShoppingCart },
  { label: "Wallet View", path: "/voult/home", icon: Wallet },
  { label: "Add Card", path: "/voult/add-card", icon: Plus },
  { label: "Token Details", path: "/voult/token-details", icon: FileText },
  { label: "Activity Log", path: "/voult/activity", icon: FileText },
  { label: "Vault Settings", path: "/voult/settings", icon: Settings },
  { label: "Usage Analytics", path: "/voult/usage-analytics", icon: BarChart2 },
  { label: "Bulk Management", path: "/voult/bulk-management", icon: Layers },
  { label: "Security Audit", path: "/voult/security-audit", icon: Shield },
  { label: "Integration Keys", path: "/voult/integration-keys", icon: Key },
  { label: "Payment History", path: "/voult/payment-history", icon: Receipt },
  { label: "Security Dashboard", path: "/voult/security-dashboard", icon: ShieldAlert },
  { label: "Merchant Access", path: "/voult/merchant-access", icon: Store },
  { label: "Security Center", path: "/voult/security-center", icon: ShieldAlert },
  { label: "API Credentials", path: "/voult/api-credentials", icon: Key },
  { label: "Transactions", path: "/voult/transactions", icon: Receipt },
];

export default function AppLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className={`flex items-center gap-2.5 px-4 py-5 ${collapsed ? "justify-center" : ""}`}>
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white text-slate-900">
          <Lock className="h-4 w-4" />
        </div>
        {!collapsed && (
          <div>
            <p className="text-sm font-semibold text-white">Voult</p>
            <p className="text-[10px] text-slate-400">Card Tokenization</p>
          </div>
        )}
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-2">
        {NAV.map(({ label, path, icon: Icon }) => {
          const active = location.pathname === path;
          return (
            <Link
              key={path}
              to={path}
              data-testid={`voult-nav-${path.split("/").pop()}`}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition ${
                active
                  ? "bg-white/15 text-white font-medium"
                  : "text-slate-400 hover:bg-white/8 hover:text-white"
              } ${collapsed ? "justify-center" : ""}`}
              title={collapsed ? label : undefined}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              {!collapsed && <span className="truncate">{label}</span>}
            </Link>
          );
        })}
      </nav>

      <div className={`border-t border-white/10 p-3 ${collapsed ? "flex justify-center" : ""}`}>
        <Link to="/" data-testid="voult-back-to-citidan"
          className={`mb-2 flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-slate-400 hover:bg-white/10 hover:text-white transition ${collapsed ? "justify-center" : ""}`}>
          <ArrowLeft className="h-3.5 w-3.5" />{!collapsed && "Back to CITIDAN"}
        </Link>
        {!collapsed && (
          <div className="mb-2 px-2">
            <p className="truncate text-xs font-medium text-white">{user?.full_name || "User"}</p>
            <p className="truncate text-[10px] text-slate-400">{user?.email}</p>
          </div>
        )}
        <button
          onClick={() => base44.auth.logout("/")}
          className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-slate-400 hover:bg-white/10 hover:text-white transition ${collapsed ? "justify-center" : ""}`}
        >
          <LogOut className="h-3.5 w-3.5" />
          {!collapsed && "Sign out"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col flex-shrink-0 bg-slate-900 transition-all duration-200 relative ${
          collapsed ? "w-16" : "w-56"
        }`}
      >
        <SidebarContent />
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="absolute -right-3 top-6 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50"
        >
          {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
        </button>
      </aside>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <div className="fixed inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative z-50 flex w-64 flex-col bg-slate-900">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-4 text-slate-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex h-14 flex-shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-4">
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          <button className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100">
            <Bell className="h-4.5 w-4.5" />
          </button>
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
            {(user?.full_name || user?.email || "U")[0].toUpperCase()}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}